/**
 * Display formatting. Money arrives from the API as a decimal string and is
 * only ever formatted here, never parsed into a float for arithmetic.
 */

/** Indian grouping: "1234567.5" → "12,34,567.50". */
export function formatMoney(value: string | null | undefined, options: { symbol?: boolean } = {}): string {
  const raw = (value ?? '0').trim() || '0'
  const negative = raw.startsWith('-')
  const [intPart = '0', decPart = ''] = raw.replace(/^-/, '').split('.')
  let integer = intPart.replace(/^0+(?=\d)/, '')

  if (integer.length > 3) {
    const lastThree = integer.slice(-3)
    const rest = integer.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
    integer = `${rest},${lastThree}`
  }

  const decimals = (decPart + '00').slice(0, 2)
  const body = `${options.symbol === false ? '' : '₹'}${integer}.${decimals}`
  return negative ? `-${body}` : body
}

const dateFormatter = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date)
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date)
}

/** "3 minutes ago", "yesterday", falling back to a date after a month. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'Never'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 45) return 'just now'
  if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute')
  if (abs < 86400) return relative.format(Math.round(seconds / 3600), 'hour')
  if (abs < 86400 * 30) return relative.format(Math.round(seconds / 86400), 'day')
  return formatDate(iso)
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

export function greeting(now: Date = new Date()): string {
  const hour = now.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/** URL-safe workspace slug: "Abc Traders & Co." → "abc-traders-co". */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}
